import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { fromTable } from "@/lib/supabase-helpers";
import { ClipboardCheck } from "lucide-react";
import { MyLessonsSection } from "./MyLessonsSection";

/**
 * FAB de check-in rapido para mobile/PWA.
 *
 * Mostra-se APENAS quando:
 *  - Viewport mobile (< sm = 640px) OU app rodando como PWA standalone
 *  - Usuario logado
 *  - Tem aluno ativo vinculado (user_id em club_students)
 *  - Tem pelo menos 1 aula matriculada para hoje
 *
 * Clique: abre sheet com lista de aulas e botoes de check-in (reuso do
 * MyLessonsSection). Experiencia "direto" prometida para PWA.
 */

const CLUB_TZ = "America/Sao_Paulo";

function isMobileOrPWA(): boolean {
  if (typeof window === "undefined") return false;
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  const isMobileViewport = window.matchMedia?.("(max-width: 640px)").matches;
  return isStandalone || isMobileViewport;
}

function todayInClubTz(): { isoDate: string; dayOfWeek: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CLUB_TZ,
    year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
  }).formatToParts(new Date());
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const dayOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(m.weekday);
  return { isoDate: `${m.year}-${m.month}-${m.day}`, dayOfWeek };
}

export function QuickCheckinFAB() {
  const { t } = useTranslation("myLessons");
  const [shouldShow, setShouldShow] = useState(false);
  const [hasLessonToday, setHasLessonToday] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setShouldShow(isMobileOrPWA());
    const onResize = () => setShouldShow(isMobileOrPWA());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!shouldShow) return;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      const { data: students } = await supabase
        .from("club_students")
        .select("id")
        .eq("user_id", auth.user.id)
        .eq("status", "active");
      const ids = (students ?? []).map((s) => s.id);
      if (ids.length === 0) return;

      const { isoDate, dayOfWeek } = todayInClubTz();
      const { data: enr } = await fromTable("club_lesson_students")
        .select("club_lessons(id, day_of_week, is_active, specific_date, is_recurring)")
        .in("student_id", ids);
      type LessonRow = {
        club_lessons: {
          id: string;
          day_of_week: number;
          is_active: boolean;
          specific_date: string | null;
          is_recurring: boolean;
        } | null;
      };
      const today = ((enr ?? []) as LessonRow[]).some((row) => {
        const l = row.club_lessons;
        if (!l || !l.is_active) return false;
        return l.is_recurring ? l.day_of_week === dayOfWeek : l.specific_date === isoDate;
      });
      setHasLessonToday(today);
    })();
  }, [shouldShow]);

  if (!shouldShow || !hasLessonToday) return null;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 h-14 rounded-full shadow-lg gap-2 sm:hidden"
        size="lg"
        aria-label={t("myLessons.title")}
      >
        <ClipboardCheck className="h-5 w-5" />
        {t("myLessons.quickCheckin")}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              {t("myLessons.title")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <MyLessonsSection />
          </div>
          <div className="mt-4">
            <Button variant="outline" className="w-full" asChild onClick={() => setOpen(false)}>
              <Link to="/perfil?tab=lessons">{t("myLessons.viewAllLessons")}</Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
